import { Component, computed, effect, signal } from '@angular/core'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { format, getTime } from 'date-fns'
import { ObstaclesMetrics } from '../../enums/obstacle-metrics.enum'
import { WarningDataModel } from 'src/app/shared/models/warning-event.model'
import { calculateDirections } from 'src/app/shared/utils/geo.utils'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { get } from 'lodash'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'

@Component({
  selector: 'app-obstacle-road-details',
  templateUrl: './obstacle-road-details.component.html',
  standalone: false,
})
export class ObstacleRoadDetailsComponent {
  public ObstacleMetrics = ObstaclesMetrics
  private readonly _details = signal<Map<string, DetailsModel>>(new Map<string, DetailsModel>())
  public readonly details = this._details.asReadonly()
  public hasDetails = computed(() => this.details().size > 0)
  public rawFeature: string | undefined
  public features: MapGeoJSONFeature[] = []
  public zoomLevel: number | undefined
  public index: number = 0
  public bearing = ''
  public icon = 'rb-ic rb-ic-arrow-down-frame fs-2xl'
  public selectedFeatureId = ''

  constructor(
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
  ) {
    effect(() => {
      const features = this.mapEventService.clickedFeatures()
      this._details.set(new Map<string, DetailsModel>())
      this.features.length != features.length ? (this.index = 0) : this.index
      this.features = features
      if (features[this.index]?.layer?.id === 'obstacle-lines') {
        this.bearing = calculateDirections(features[this.index]).start.bearing
        this.setCurrentFeature(this.index)
      }
    })
  }

  toggleFeature(index: number) {
    this.index = index
    this.setCurrentFeature(index)
  }

  setCurrentFeature(index: number = this.index) {
    this.index = index
    this.selectedFeatureId =
      this.features[this.index].id?.toString() !== '0'
        ? this.features[this.index].id?.toString() || '-'
        : '-'
    this.rawFeature = JSON.stringify(this.features[this.index], undefined, 2)
    const properties: WarningDataModel = this.features[this.index].properties as WarningDataModel
    this.mapWarningsToDetails(properties)
    this.appState.setState(AppPaths.Obstacle, {
      selectedFeatureId: this.selectedFeatureId,
    })
  }

  mapWarningsToDetails(properties: WarningDataModel) {
    this.setDetail('RawMetric', {
      title: 'Hazards',
      items: [
        {
          description: 'Trace ID',
          value: properties.traceId,
        },
        {
          description: 'Version',
          value: properties.version ? properties.version.toString() : '-',
        },
        {
          description: 'Segment ID',
          value: properties.segmentId,
        },
        {
          description: 'Type',
          value: properties.name,
        },
        {
          description: 'Probability',
          value: Math.round(properties.probability * 100).toString(),
          unit: '%',
        },
        {
          description: `Valid time (${getTimeZoneLabel(new Date(properties['startTime']))})`,
          value: `${
            properties['startTime']
              ? format(new Date(properties['startTime']), 'yyyy-MM-dd HH:mm')
              : '-'
          } - ${
            properties['expiryTime']
              ? format(new Date(properties['expiryTime']), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
      ],
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this._details.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  close() {
    this._details.set(new Map<string, DetailsModel>())
    this.appState.setState(AppPaths.Obstacle, {
      selectedFeatureId: undefined,
    })
  }
}
